'use client';

import type { ListDetail } from '@swishapp/api-client';
import { useMemo } from 'react';
import { SanityImage } from '../../../../sanity/utils/components/sanity-image';
import type { SharedWishlistCardData } from '../../../wishlist-card';
import { useWishlistProducts } from './use-wishlist-products';

interface BoardCardProps {
  board: ListDetail;
  onClick?: (board: ListDetail) => void;
}

export function BoardCard({ board, onClick }: BoardCardProps) {
  // Get product GIDs for this specific board
  const productGids = useMemo(() => {
    if (!board.items) return [];
    const gids = board.items.map(
      (item) => `gid://shopify/Product/${item.productId}`
    );
    return Array.from(new Set(gids));
  }, [board.items]);

  // Fetch product data for preview
  const { data: products } = useWishlistProducts();

  // Get products that belong to this board
  const boardProducts = useMemo(() => {
    if (!products || !productGids.length) return [];
    return (products as SharedWishlistCardData[])
      .filter((product) => productGids.includes(product.productGid || ''))
      .slice(0, 4); // Show max 4 product previews
  }, [products, productGids]);

  return (
    <button
      type="button"
      onClick={() => onClick?.(board)}
      className="group relative w-full overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[hsl(var(--color))] focus:ring-offset-2"
      aria-label={`Open ${board.name} board`}
    >
      {/* Product Preview Grid */}
      <div className="relative aspect-square bg-gray-50">
        {boardProducts.length > 0 ? (
          <div
            className={`grid h-full gap-0.5 p-2 ${
              boardProducts.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
            }`}
          >
            {boardProducts.slice(0, 4).map((product, index) => (
              <div
                key={product._id || index}
                className="relative overflow-hidden rounded bg-white"
              >
                {product.mainImage ? (
                  <SanityImage
                    image={product.mainImage}
                    className="h-full w-full object-cover"
                    sizes="(max-width: 768px) 25vw, 12vw"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gray-100">
                    <div className="h-8 w-8 rounded bg-gray-200" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Empty state
          <div className="flex h-full flex-col items-center justify-center px-4 py-6 text-gray-400">
            <svg
              className="mb-2 h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Empty Board</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <p className="text-xs">Empty Board</p>
          </div>
        )}
      </div>

      {/* Board Info */}
      <div className="w-full p-3 text-left">
        <h3 className="truncate font-medium text-gray-900">{board.name}</h3>
        <p className="mt-1 text-gray-500 text-sm">
          {board.items?.length || 0}{' '}
          {board.items?.length === 1 ? 'item' : 'items'}
        </p>

        {/* Show first few product names as preview */}
        {boardProducts.length > 0 && (
          <div className="mt-2">
            <p className="truncate text-gray-400 text-xs">
              {boardProducts
                .slice(0, 2)
                .map((p) => p.title)
                .filter(Boolean)
                .join(', ')}
              {boardProducts.length > 2 && '...'}
            </p>
          </div>
        )}
      </div>
    </button>
  );
}
